import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Clock, AlertTriangle, CheckCircle, XCircle, Users, Crown, ArrowUpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { format, parseISO } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Separate component for child limit warning
const ChildLimitBanner = () => {
  const { isChildLimitReached, childCount, maxChildren, subscription } = useSubscription();
  const { language } = useLanguage();
  
  // Don't show for unlimited plans or owner accounts
  if (!isChildLimitReached || maxChildren === 'unlimited' || subscription?.isOwnerAccount) {
    return null;
  }

  const pct = maxChildren > 0 ? Math.min(100, Math.round((childCount / maxChildren) * 100)) : 100;

  return (
    <div
      className="px-4 py-3 border-y"
      style={{
        background: 'linear-gradient(90deg, #fef3c7 0%, #fde68a 100%)',
        borderColor: '#f59e0b',
      }}
      data-testid="child-limit-banner"
    >
      <div className="max-w-5xl mx-auto flex items-center gap-3 flex-wrap">
        <div className="p-2 rounded-full bg-white/70 flex-shrink-0">
          <Users className="w-5 h-5 text-amber-700" />
        </div>
        <div className="flex-1 min-w-[200px]">
          <p className="text-amber-900 font-bold text-sm">
            {language === 'es' ? 'Estás en la capacidad de tu plan' : "You're at your plan capacity"}
          </p>
          <p className="text-amber-800 text-xs mt-0.5">
            {language === 'es'
              ? `${childCount} de ${maxChildren} niños inscritos`
              : `${childCount} of ${maxChildren} children enrolled`}
          </p>
          <div className="mt-2 h-2 w-full rounded-full bg-amber-200/70 overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${pct}%`, background: 'linear-gradient(90deg, #f59e0b, #d97706)' }}
            />
          </div>
        </div>
        <Link
          to="/pricing"
          className="flex-shrink-0 inline-flex items-center gap-1.5 bg-amber-700 hover:bg-amber-800 text-white px-4 py-2 rounded-lg font-semibold text-sm transition-colors shadow-sm"
          data-testid="child-limit-see-plans-btn"
        >
          <ArrowUpCircle className="w-4 h-4" />
          {language === 'es' ? 'Ver Planes' : 'See Plans & Pricing'}
        </Link>
      </div>
    </div>
  );
};

const TrialBanner = () => {
  const { subscription, isTrialExpired, isActive, isChildLimitReached, refreshSubscription } = useSubscription();
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cancelling, setCancelling] = useState(false);
  const [reactivating, setReactivating] = useState(false);

  // Check if user is owner only (not co-owner) for upgrade button
  const isOwnerOnly = user?.role === 'owner';

  if (!subscription) return null;

  // Don't show banner for lifetime/owner accounts
  if (subscription.billingCycle === 'lifetime' || subscription.isOwnerAccount) {
    // Format plan name for display (capitalize first letter)
    const planDisplayName = subscription.plan 
      ? subscription.plan.charAt(0).toUpperCase() + subscription.plan.slice(1)
      : 'Premium';
    
    return (
      <>
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-4 py-3 text-center">
          <div className="flex items-center justify-center gap-3">
            <Crown className="w-5 h-5" />
            <span className="font-semibold">
              {language === 'es' 
                ? `Cuenta de Propietario - Acceso Permanente (${planDisplayName})` 
                : `Owner Account - Permanent (${planDisplayName}) Access`}
            </span>
            {isOwnerOnly && (
              <button
                onClick={() => navigate("/pricing")}
                className="ml-4 inline-flex items-center gap-1.5 bg-white/20 hover:bg-white/30 text-white px-4 py-1.5 rounded-full text-sm font-semibold transition-all"
              >
                <ArrowUpCircle className="w-4 h-4" />
                {language === 'es' ? 'Actualizar' : 'Upgrade'}
              </button>
            )}
          </div>
        </div>
        <ChildLimitBanner />
      </>
    );
  }

  const handleCancel = async () => {
    const confirmMsg = language === 'es'
      ? '¿Está seguro de cancelar su suscripción? Mantendrá acceso hasta el final de su período pagado.'
      : 'Are you sure you want to cancel? You will keep access until the end of your paid period.';
    
    if (!window.confirm(confirmMsg)) return;
    
    setCancelling(true);
    try {
      await axios.post(`${API}/subscription/cancel`);
      toast.success(language === 'es' 
        ? 'Suscripción cancelada. Mantendrá acceso hasta el fin del período pagado.'
        : 'Subscription cancelled. You will keep access until your paid period ends.');
      refreshSubscription();
    } catch (error) {
      console.error('Error cancelling:', error);
      toast.error(language === 'es' ? 'Error al cancelar' : 'Failed to cancel subscription');
    }
    setCancelling(false);
  };

  const handleReactivate = async () => {
    setReactivating(true);
    try {
      await axios.post(`${API}/subscription/reactivate`);
      toast.success(language === 'es' ? 'Suscripción reactivada' : 'Subscription reactivated');
      refreshSubscription();
    } catch (error) {
      console.error('Error reactivating:', error);
      toast.error(language === 'es' ? 'Error al reactivar' : 'Failed to reactivate');
    }
    setReactivating(false);
  };

  // Format end date
  const formatEndDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return format(parseISO(dateStr), 'MMMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  // CANCELLED SUBSCRIPTION - Show when subscription will end
  if (subscription.status === 'cancelled') {
    const endDate = formatEndDate(subscription.subscriptionEndDate);
    const daysLeft = subscription.subscriptionDaysRemaining || 0;
    
    return (
      <>
        <div className="bg-orange-500 text-white px-4 py-3 text-center">
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <XCircle className="w-5 h-5" />
            <span className="font-semibold">
              {language === 'es' ? 'Suscripción Cancelada' : 'Subscription Cancelled'}
            </span>
            <span>—</span>
            <span>
              {language === 'es' 
                ? `Acceso hasta: ${endDate} (${daysLeft} días restantes)`
                : `Access until: ${endDate} (${daysLeft} days remaining)`}
            </span>
            <Button
              onClick={handleReactivate}
              disabled={reactivating}
              size="sm"
              className="bg-white text-orange-600 hover:bg-orange-100 ml-2"
            >
              {reactivating 
                ? (language === 'es' ? 'Reactivando...' : 'Reactivating...') 
                : (language === 'es' ? 'Reactivar' : 'Reactivate')}
            </Button>
          </div>
        </div>
        <ChildLimitBanner />
      </>
    );
  }

  // EXPIRED PAID SUBSCRIPTION - end date passed (active+expired flag OR persisted status): no Cancel, prompt to resubscribe
  if ((isActive && subscription.isSubscriptionExpired) || subscription.status === 'expired') {
    return (
      <>
        <div className="bg-red-500 text-white px-4 py-3 text-center">
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <AlertTriangle className="w-5 h-5" />
            <span className="font-semibold">
              {language === 'es' ? 'Suscripción Expirada' : 'Subscription Expired'}
            </span>
            <span>—</span>
            <span>
              {language === 'es'
                ? 'Suscríbase para continuar usando la aplicación'
                : 'Subscribe to continue using the application'}
            </span>
            <Link
              to="/pricing"
              className="bg-white text-red-600 px-4 py-1 rounded-full font-semibold hover:bg-red-100 ml-2"
            >
              {language === 'es' ? 'Ver Planes' : 'View Plans'}
            </Link>
          </div>
        </div>
        <ChildLimitBanner />
      </>
    );
  }

  // ACTIVE SUBSCRIPTION - Show plan info and cancel option
  if (isActive) {
    const plan = subscription.plan?.charAt(0).toUpperCase() + subscription.plan?.slice(1);
    const cycle = subscription.billingCycle === 'annual' 
      ? (language === 'es' ? 'Anual' : 'Yearly') 
      : (language === 'es' ? 'Mensual' : 'Monthly');
    const endDate = formatEndDate(subscription.subscriptionEndDate);
    
    return (
      <>
        <div className="bg-green-600 text-white px-4 py-3 text-center">
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <CheckCircle className="w-5 h-5" />
            <span className="font-semibold">
              {plan} {language === 'es' ? 'Plan' : 'Plan'} ({cycle})
            </span>
            {endDate && (
              <>
                <span>—</span>
                <span>
                  {language === 'es' ? `Activo hasta: ${endDate}` : `Active until: ${endDate}`}
                </span>
              </>
            )}
            <Button
              onClick={handleCancel}
              disabled={cancelling}
              size="sm"
              variant="outline"
              className="border-white text-white hover:bg-green-700 ml-2"
            >
              {cancelling 
              ? (language === 'es' ? 'Cancelando...' : 'Cancelling...') 
              : (language === 'es' ? 'Cancelar Suscripción' : 'Cancel Subscription')}
          </Button>
        </div>
      </div>
      <ChildLimitBanner />
      </>
    );
  }

  // EXPIRED TRIAL
  if (isTrialExpired) {
    return (
      <>
        <div className="bg-red-500 text-white px-4 py-3 text-center">
          <div className="flex items-center justify-center gap-3">
            <AlertTriangle className="w-5 h-5" />
            <span className="font-semibold">
              {language === 'es' ? '¡Prueba Expirada!' : 'Trial Expired!'}
            </span>
            <span>—</span>
            <span>
              {language === 'es' 
                ? 'Suscríbase para continuar usando la aplicación'
                : 'Subscribe to continue using the application'}
            </span>
            <Link 
              to="/pricing" 
              className="bg-white text-red-600 px-4 py-1 rounded-full font-semibold hover:bg-red-100 ml-2"
            >
              {language === 'es' ? 'Ver Planes' : 'View Plans'}
            </Link>
          </div>
        </div>
        <ChildLimitBanner />
      </>
    );
  }

  // TRIAL IN PROGRESS
  const daysLeft = subscription.trialDaysRemaining || 0;
  const urgencyColor = daysLeft <= 3 ? "bg-orange-500" : "bg-amber-500";
  
  return (
    <>
      <div className={`${urgencyColor} text-white px-4 py-3 text-center`}>
        <div className="flex items-center justify-center gap-3">
          <Clock className="w-5 h-5" />
          <span className="font-semibold">
            {language === 'es' ? 'Período de Prueba' : 'Trial Period'}
          </span>
          <span>—</span>
          <span>
            {daysLeft} {daysLeft === 1 
              ? (language === 'es' ? 'día restante' : 'day left')
              : (language === 'es' ? 'días restantes' : 'days left')
            }
          </span>
          <Link 
            to="/pricing" 
            className="bg-white text-amber-600 px-4 py-1 rounded-full font-semibold hover:bg-amber-100 ml-2"
          >
            {language === 'es' ? 'Suscribirse' : 'Subscribe Now'}
          </Link>
        </div>
      </div>
      <ChildLimitBanner />
    </>
  );
};

export default TrialBanner;
